import { useFormik } from "formik";
import * as yup from "yup";
import { Box, Button, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslate } from "../../../../../hooks/useTranslate";
import useAuth from "../../../../../hooks/context/useAuth";
import { EmailField, GenericInput, PasswordField } from "../../../Molecules";
import useToast from "../../../../../hooks/context/useToast";
import { createUserService } from "../../../../../services/createUserService";
import { colors } from "../../../../../theme/colors";
import { BreakpointBox } from "../../../Atoms";

export function NewUserBox() {
  const { strings } = useTranslate();
  const { language } = useAuth();
  const navigate = useNavigate();
  const { setOpen, setToastData } = useToast();

  const validationSchema = yup.object({
    name: yup
      .string()
      .required(strings.validationFields.name.required[language]),
    phoneCode: yup
      .string()
      .required(strings.validationFields.phoneCode.required[language]),
    phone: yup
      .number()
      .typeError(strings.validationFields.phoneCode.typeError[language])
      .required(strings.validationFields.phoneCode.required[language]),
    ssn: yup.string().required(strings.validationFields.ssn[language]),
    email: yup
      .string()
      .email(strings.validationFields.email[language])
      .required(strings.validationFields.email.required[language]),
    password: yup
      .string()
      .required(strings.validationFields.password[language]),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      phoneCode: "55",
      phone: "",
      ssn: "",
      email: "",
      password: "",
    },
    validationSchema,
    async onSubmit(values: any) {
      try {
        const formatPhone = `${values?.phoneCode}${values?.phone}`;
        await createUserService({ ...values, phone: formatPhone });

        setToastData({
          anchor: { vertical: "top", horizontal: "center" },
          duration: 5000,
          type: "success",
          message: strings.toastMessages.welcomeNew[language],
        });
        setOpen(true);
        navigate("/login");
      } catch (e) {
        setToastData({
          anchor: { vertical: "top", horizontal: "center" },
          duration: 5000,
          type: "error",
          message: strings.toastMessages.emailAlreadyExists[language],
        });
        setOpen(true);
      }
    },
  });

  const handleCreateUser = () => {
    formik.handleSubmit();
  };

  return (
    <Box>
      <Typography color={colors.purple[500]} fontWeight={700} fontSize={50}>
        Cadastro
      </Typography>
      <Box
        sx={{
          p: 3,
          background: "#FFFFFF",
          borderRadius: 5,
        }}
      >
        <GenericInput
          hasIcon={false}
          onChange={(value, name) => formik.setFieldValue(name, value)}
          error={formik.touched.name && formik.errors.name ? true : false}
          errorMessage={formik.touched.name ? formik.errors.name : ""}
          value={formik.values.name}
          placeholder="Nome"
          inputName="name"
        />

        <BreakpointBox>
          <Box display="flex" sx={{ width: "100%" }}>
            <GenericInput
              hasIcon={false}
              onChange={(value, name) => formik.setFieldValue(name, value)}
              error={
                (formik.touched.phone && formik.errors.phone) ||
                (formik.touched.phoneCode && formik.errors.phoneCode)
                  ? true
                  : false
              }
              errorMessage={
                formik.touched.phone || formik.touched.phoneCode
                  ? formik.errors.phone
                    ? formik.errors.phone
                    : formik.errors.phoneCode
                    ? formik.errors.phoneCode
                    : ""
                  : ""
              }
              value={formik.values.phone}
              placeholder="Telefone com DDD"
              inputName="phone"
            />
          </Box>
          <GenericInput
            hasIcon={false}
            onChange={(value, name) => formik.setFieldValue(name, value)}
            error={formik.touched.ssn && formik.errors.ssn ? true : false}
            errorMessage={formik.touched.ssn ? formik.errors.ssn : ""}
            value={formik.values.ssn}
            placeholder="CPF(exclusive to Brazil)"
            inputName="ssn"
          />
        </BreakpointBox>

        <EmailField
          hasIcon={false}
          onChange={(value) => formik.setFieldValue("email", value)}
          error={formik.touched.email && formik.errors.email ? true : false}
          errorMessage={
            formik.touched.email ? (formik.errors.email as string) : ""
          }
          value={formik.values.email}
          placeholder="E-mail"
        />
        <PasswordField
          hasIcon={false}
          onChange={(value) => formik.setFieldValue("password", value)}
          error={
            formik.touched.password && formik.errors.password ? true : false
          }
          errorMessage={
            formik.touched.password ? (formik.errors.password as string) : ""
          }
          value={formik.values.password}
          placeholder="Senha"
        />

        <Button
          variant="contained"
          id="login-btn"
          onClick={handleCreateUser}
          style={{ marginTop: 12 }}
          fullWidth
        >
          {strings.buttons.createLogin[language]}
        </Button>
      </Box>
    </Box>
  );
}
